import { StrictMode, useEffect, lazy, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { msalInstance } from './platform/lib/msalConfig'
import { initTelemetry } from './platform/lib/telemetry'
import { captureRedirectErrors } from './platform/lib/authRedirectError'
import { ProfileContext, useProfileLoader } from './platform/hooks/useProfile'
import { AuthGuard } from './platform/components/auth/AuthGuard'
import { AccessDenied } from './platform/components/auth/AccessDenied'
import { AppShell } from './platform/components/layout/AppShell'
// Spec 133 — DÉCOUVERTE DES SOLUTIONS, par motif et non par nom. Cet import est là pour son effet
// de bord : il charge `src/solutions/*/register.tsx`, qui s'inscrivent au registre. Il doit venir
// AVANT toute lecture du registre (navigation, routes). `main.tsx` est la racine de composition —
// le seul endroit qui assemble, et il le fait sans nommer aucune solution.
import './platform/lib/solutionsLoader'
import { getSolutionRoutes } from './platform/lib/solutionRegistry'
import { LoginPage } from './platform/pages/LoginPage'
import { NotFound } from './platform/pages/NotFound'

// US2 (073) — Code-splitting des routes : chaque page est chargée à la demande (React.lazy)
// pour réduire le JS initial (chunk unique ~1,8 Mo). Le rendu pendant le chargement d'un
// chunk est couvert par le <Suspense> autour de l'Outlet dans AppShell. Les composants du
// shell/auth (LoginPage, AccessDenied, AppShell, AuthGuard, NotFound) restent en static
// (chemin de premier rendu / fallback).
function lazyPage(loader: () => Promise<Record<string, unknown>>, name: string) {
  return lazy(() => loader().then(m => ({ default: m[name] as ComponentType })))
}

// Spec 161 — le Tableau de bord de la plateforme remplace « Vue d'ensemble » ; la page d'arrivée dépend du rôle.
const TableauDeBordPage = lazyPage(() => import('./platform/pages/TableauDeBordPage'), 'TableauDeBordPage')
const PageArrivee = lazyPage(() => import('./platform/pages/PageArrivee'), 'PageArrivee')
const AlertesPage = lazyPage(() => import('./platform/pages/AlertesPage'), 'AlertesPage')
const PortfolioPage = lazyPage(() => import('./platform/pages/PortfolioPage'), 'PortfolioPage')
const AdminClientsHub = lazyPage(() => import('./platform/pages/admin/hubs/AdminClientsHub'), 'AdminClientsHub')
const AdminRevendeursHub = lazyPage(() => import('./platform/pages/admin/hubs/AdminRevendeursHub'), 'AdminRevendeursHub')
const AdminFinOpsHub = lazyPage(() => import('./platform/pages/admin/hubs/AdminFinOpsHub'), 'AdminFinOpsHub')
const AdminNotificationsHub = lazyPage(() => import('./platform/pages/admin/hubs/AdminNotificationsHub'), 'AdminNotificationsHub')
const AdminUtilisateursHub = lazyPage(() => import('./platform/pages/admin/hubs/AdminUtilisateursHub'), 'AdminUtilisateursHub')
const TenantDetailPage = lazyPage(() => import('./platform/pages/admin/TenantDetailPage'), 'TenantDetailPage')
const ResellerDetailPage = lazyPage(() => import('./platform/pages/admin/ResellerDetailPage'), 'ResellerDetailPage')
const AdminUserDetailPage = lazyPage(() => import('./platform/pages/admin/AdminUserDetailPage'), 'AdminUserDetailPage')
const AdminOnboardingPage = lazyPage(() => import('./platform/pages/admin/onboarding/AdminOnboardingPage'), 'AdminOnboardingPage')
const AdminOnboardingAssistantPage = lazyPage(() => import('./platform/pages/admin/onboarding/AdminOnboardingAssistantPage'), 'AdminOnboardingAssistantPage')
const SettingsPage = lazyPage(() => import('./platform/pages/SettingsPage'), 'SettingsPage')
const AidePage = lazyPage(() => import('./platform/pages/AidePage'), 'AidePage')
const ResellerClientDetailPage = lazyPage(() => import('./platform/pages/reseller/ResellerClientDetailPage'), 'ResellerClientDetailPage')
const ResellerClientUserDetailPage = lazyPage(() => import('./platform/pages/reseller/ResellerClientUserDetailPage'), 'ResellerClientUserDetailPage')
const ResellerAdminPage = lazyPage(() => import('./platform/pages/reseller/ResellerAdminPage'), 'ResellerAdminPage')
const ResellerUserDetailPage = lazyPage(() => import('./platform/pages/reseller/ResellerUserDetailPage'), 'ResellerUserDetailPage')
const OnboardingPage = lazyPage(() => import('./platform/pages/reseller/OnboardingPage'), 'OnboardingPage')
const ResellerRecurrencePage = lazyPage(() => import('./platform/pages/reseller/ResellerRecurrencePage'), 'ResellerRecurrencePage')
const OnboardingAssistantPage = lazyPage(() => import('./platform/pages/reseller/OnboardingAssistantPage'), 'OnboardingAssistantPage')
import { TooltipProvider } from './platform/components/ui/tooltip'
import { applyTheme, getInitialTheme } from './platform/lib/theme'
import './index.css'

// Init thème anti-flash (spec 046 D-DarkMode-01) — applique la classe .dark
// depuis le cache localStorage AVANT le render React (évite le flash light → dark).
applyTheme(getInitialTheme())

// eslint-disable-next-line react-refresh/only-export-components -- composant du point d'entrée (extraction disproportionnée)
function UserDetailRedirect() {
  const { objectId } = useParams<{ objectId: string }>()
  // Ancienne route `/utilisateurs/:objectId` → liste parente avec Sheet ouvert
  return <Navigate to={`/licences-utilisateurs?user=${encodeURIComponent(objectId ?? '')}`} replace />
}

// F-09 (065) : `navigateToLoginRequestUrl: true` fait que MSAL restaure lui-même le
// deep-link demandé avant le login. Un `<Navigate to="/">` immédiat sur /auth/callback
// entrait en course avec cette restauration → atterrissage sur l'accueil au lieu de la
// page demandée. On rend un écran passif et on ne se replie sur l'accueil que si MSAL a
// terminé sans nous avoir fait quitter /auth/callback (ex. ouverture directe de la route,
// sans URL mémorisée). Si MSAL navigue vers le deep-link, ce composant se démonte et le
// repli est annulé.
// eslint-disable-next-line react-refresh/only-export-components -- composant du point d'entrée (extraction disproportionnée)
function AuthCallback() {
  const { inProgress } = useMsal()
  const navigate = useNavigate()
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return
    const timer = setTimeout(() => navigate('/', { replace: true }), 400)
    return () => clearTimeout(timer)
  }, [inProgress, navigate])
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <p className="text-muted-foreground font-body">Connexion en cours...</p>
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- composant du point d'entrée (extraction disproportionnée)
function AppWithProfile() {
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()
  const ready = isAuthenticated && inProgress === InteractionStatus.None
  const profileState = useProfileLoader(ready)

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <p className="text-muted-foreground font-body">Connexion en cours...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    )
  }

  return (
    <ProfileContext.Provider value={profileState}>
      <Routes>
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/*"
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          <Route index element={<PageArrivee />} />
          <Route path="overview" element={<TableauDeBordPage />} />
          {/* Routes des solutions inscrites — le socle les monte sans en connaître aucune. */}
          {getSolutionRoutes().map(r => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="utilisateurs/:objectId" element={<UserDetailRedirect />} />
          <Route path="alertes" element={<AlertesPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          {/* Spec 113 — menu Administration : /admin redirige vers le 1er domaine ; hubs par domaine */}
          <Route path="admin" element={<Navigate to="/admin/clients" replace />} />
          <Route path="admin/clients" element={<AdminClientsHub />} />
          <Route path="admin/revendeurs" element={<AdminRevendeursHub />} />
          <Route path="admin/finops" element={<AdminFinOpsHub />} />
          <Route path="admin/notifications" element={<AdminNotificationsHub />} />
          <Route path="admin/utilisateurs" element={<AdminUtilisateursHub />} />
          <Route path="admin/onboarding" element={<AdminOnboardingPage />} />
          <Route path="admin/onboarding/new" element={<AdminOnboardingAssistantPage />} />
          <Route path="admin/onboarding/:accountId" element={<AdminOnboardingAssistantPage />} />
          <Route path="admin/clients/:accountId" element={<TenantDetailPage />} />
          <Route path="admin/revendeurs/:accountId" element={<ResellerDetailPage />} />
          <Route path="admin/utilisateurs/:userId" element={<AdminUserDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="aide" element={<AidePage />} />
          <Route path="reseller/admin" element={<ResellerAdminPage />} />
          <Route path="reseller/admin/users/:userId" element={<ResellerUserDetailPage />} />
          <Route path="reseller/onboarding" element={<OnboardingPage />} />
          <Route path="reseller/recurrence" element={<ResellerRecurrencePage />} />
          <Route path="reseller/onboarding/new" element={<OnboardingAssistantPage />} />
          <Route path="reseller/onboarding/:accountId" element={<OnboardingAssistantPage />} />
          <Route path="reseller/clients/:accountId" element={<ResellerClientDetailPage />} />
          <Route path="reseller/clients/:accountId/users/:userId" element={<ResellerClientUserDetailPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ProfileContext.Provider>
  )
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root element not found')

initTelemetry()
// Issue #43 — l'abonnement DOIT précéder `createRoot` : `MsalProvider` déclenche
// `handleRedirectPromise()` dans son effet, et `emitEvent` ne notifie que les abonnés déjà
// enregistrés. Un composant, qui s'abonne forcément plus tard, ne verrait jamais l'échec.
captureRedirectErrors(msalInstance)
msalInstance.initialize()
  .then(() => {
    createRoot(rootEl).render(
      <StrictMode>
        <TooltipProvider delayDuration={400}>
          <MsalProvider instance={msalInstance}>
            <BrowserRouter>
              <AppWithProfile />
            </BrowserRouter>
          </MsalProvider>
        </TooltipProvider>
      </StrictMode>
    )
  })
  .catch(err => {
    console.error('[MSAL] Initialization failed:', err)
  })
